/**
 * riderLayout.ts — layout model for the Bandz rider PDF generator.
 *
 * The rider PDF (buildPdfDoc in src/pages/RiderPage.tsx) is assembled from a
 * list of named sections. This module defines that model so an admin UI can
 * reorder / toggle sections and edit fixed texts & style (WYSIWYG later),
 * without touching the generator code.
 *
 * - createDefaultPdfLayout() reproduces the EXACT hard-coded behaviour of the
 *   original generator (all 17 sections, current order, Sound + Stage Plot
 *   starting a new page).
 * - mergePdfLayouts() layers a persisted (partial) admin override on top of
 *   the defaults: texts/style are shallow-merged per key, the sections array
 *   wins WHOLESALE (it is the full ordered list).
 */

/** All sections the rider PDF can emit, in the current render order. */
export type PdfSectionId =
  | 'title'
  | 'contact'
  | 'intro'
  | 'general-info'
  | 'hospitality'
  | 'technical-rider'
  | 'sound'
  | 'iem'
  | 'monitors'
  | 'audio-backline'
  | 'wireless'
  | 'backline'
  | 'mic-stands'
  | 'mic-provided'
  | 'stageplan'
  | 'input-list'
  | 'output-list'
  | 'rack-usage'
  | 'stage-plot'
  | 'lights'
  | 'light-plot'
  | 'stage-design';

/** One section of the rider: enabled flag, optional title override, optional page break. */
export interface PdfLayoutSection {
  id: PdfSectionId;
  enabled: boolean;
  /** When a non-empty string, replaces the default section heading text. */
  title?: string | null;
  /** Start the section on a new page. */
  pageBreakBefore?: boolean;
  /**
   * Per-section style override (2026-09-15): keys present here override the
   * document's global style FOR THIS SECTION ONLY (heading color/size, body
   * font size, colors, footer size/color, font family…). Absent keys inherit
   * the global style — an empty/undefined override renders exactly like the
   * global style.
   */
  styleOverride?: Partial<PdfLayoutStyle>;
  /**
   * Ordered list of the section's renderable fields. When absent the section
   * renders ALL its fields in the engine's default order (back-compat with
   * layouts saved before field-level editing existed). When present, only
   * enabled fields render, in list order — this is how the editor lets you
   * "place each field as you want".
   */
  fields?: PdfLayoutField[];
}

/** A single renderable field inside a rider section. */
export interface PdfLayoutField {
  /** Stable engine id, e.g. 'general-info.show-duration'. */
  id: string;
  enabled: boolean;
  /** Label shown in the editor (null = engine default label). */
  label?: string | null;
  /** Relative band URL to the config page that edits this field's value, e.g. 'config/general'. */
  link?: string | null;
  /** Spacer fields only: vertical gap height in pdfmake units (default 8). */
  size?: number;
  /** Free-text fields only (id 'text'): the paragraph content. */
  textContent?: string;
  /** Free-text fields only: bold paragraph. */
  bold?: boolean;
  /** Free-text fields only: italic paragraph. */
  italic?: boolean;
  /** Free-text fields only: font size override (default = body font size). */
  fontSize?: number;
  /** Free-text fields only: horizontal alignment. */
  align?: 'left' | 'center' | 'right';
  /**
   * PER-FIELD style override (2026-09-16): the same partial style shape as
   * `PdfLayoutSection.styleOverride`, applied to THIS field only.
   * Cascade: global style → section.styleOverride → field.styleOverride
   * (the field wins). The editor exposes a subset of keys for a field:
   * `fontFamily` (Font), `bodyFontSize` (Size), `textColor` (Color),
   * `fieldBold` (Bold), `fieldItalic` (Italic), `fieldAlign` (Align).
   *
   * LEGACY MAPPING (fields saved before this feature): the free-text fields
   * already carried flat `fontSize` / `bold` / `italic` / `align`. They keep
   * working and are read as a SOFT default, key by key:
   *   field.fontSize    → bodyFontSize
   *   field.bold        → fieldBold
   *   field.italic      → fieldItalic
   *   field.align       → fieldAlign
   * `fieldStyleOverride()` returns both merged, `styleOverride` winning.
   * Nothing else changes: an absent override leaves the field's nodes
   * untouched (default render, byte-identical).
   */
  styleOverride?: Partial<PdfLayoutStyle>;
  /**
   * TABLE-COLUMN fields only (input-list / output-list / rack-usage / backline):
   * the column's WIDTH in pdfmake units, overriding the engine default.
   *
   * Absent = the column keeps its DEFAULT width, defined by the engine
   * (`ListColumn.width` / the table's `widths[]`): a fixed number, `'*'`
   * (share the remaining space equally) or `'auto'` (natural content width,
   * including the header — never clipped). A persisted number here wins over
   * the default; the editor shows a numeric field per column plus a ↺ reset
   * that removes the override (back to the default).
   *
   * Nothing is backfilled at merge: absent = default (no migration, no
   * regression on layouts saved before this field existed).
   */
  width?: number;
}

/** Fixed texts of the rider. {token} placeholders are filled at render time. */
export interface PdfLayoutTexts {
  /** Appended to the band name for the document title, e.g. 'TECH & HOSPITALITY RIDER 2026'. */
  titleSuffix: string;
  /** Label of the Management / Advancing contact block. */
  contactLabel: string;
  /** Optional overrides for the Management/Advancing contact. When empty the
   * contact is taken automatically from the band's manager (Team, manager tag).
   * Useful when the same manager uses a band-specific email/phone/address. */
  contactOverrideName: string;
  contactOverrideEmail: string;
  contactOverridePhone: string;
  /** Legal intro sentence under the title (before GENERAL INFORMATION). */
  introText: string;
  /** Technical intro — own TECHNICAL RIDER section (above FOH & MON). Fallback:
   * the legacy SoundConfig.intro value until the band re-edits it. */
  techIntroText?: string;
  /** Subtitle template under the title; {versionDetail} = e.g. 'van, 2 horns'.
   *  On a rider generated FOR A SHOW the {showName} and {showDate} tokens are
   *  also resolved (showDate formatted in the active PDF locale); on a
   *  band-level rider they render empty and the orphan separators around them
   *  are cleaned up (e.g. '{showName} — {versionDetail}' → '(version: van)'). */
  versionTemplate: string;
  /** Footer left; {bandName} placeholder. */
  footerLeftTemplate: string;
  /** Footer center; {versionLabel} placeholder (band version, e.g. 'v30.04.26'). */
  footerCenterTemplate: string;
  /** Footer right; {page} and {pages} placeholders. */
  footerRightTemplate: string;
  /** When true (default), the footer is printed at the bottom of EVERY page.
   * Uncheck it to remove the footer entirely — e.g. a rider handed over without
   * the "PAGE x / y" band, or when the venue template brings its own footer.
   * Optional for back-compat: absent is treated as enabled. */
  footerEnabled?: boolean;
  /** When true, a running header (like the footer) is printed at the top of
   * EVERY page. Off by default so the original look is unchanged. */
  headerEnabled: boolean;
  /** Running header left; {bandName} placeholder. */
  headerLeftTemplate: string;
  /** Running header center; {versionLabel} placeholder. */
  headerCenterTemplate: string;
  /** Running header right; {page} and {pages} placeholders. */
  headerRightTemplate: string;
  /** When true (default), the tour name (if the show belongs to a tour) is
   * rendered under the logo, before the title. Set to false to hide it. */
  showTourName: boolean;
  /** INPUT-LIST PATCH MARKS legend (2026-10-23, user v2): ONE text line PER
   *  colour — key = the colour (hex, lowercase), value = its meaning (e.g.
   *  '#e05a4e': 'patch on the house stagebox'). Printed under the Input List
   *  as swatch + text, one row per colour ACTUALLY used on a marked track.
   *  (Supersedes the single-text inputListPatchLegend, kept for back-compat.) */
  inputListPatchLegends?: Record<string, string>;
  /** OUTPUT-LIST PATCH MARKS legend (2026-10-23, user v3): same shape as
   *  inputListPatchLegends but for the Output List (returns) — a colour can
   *  mean something DIFFERENT on the inputs than on the outputs (separate
   *  texts per list; the colours stay a shared pool). Printed under the
   *  Output List as swatch + text, one row per colour used on a marked
   *  output track (IEM / wedges / sub).
   *  (Supersedes the single-text inputListPatchLegend, kept for back-compat.) */
  outputListPatchLegends?: Record<string, string>;
}

/** Font families the rider PDF can embed (server-side pdfMake.fonts). */
export type RiderFontFamily = 'Roboto' | 'Helvetica' | 'Times' | 'Courier';

/** Visual style of the rider PDF. Values match the original hard-coded generator. */
export interface PdfLayoutStyle {
  headingColor: string;
  headingFontSize: number;
  ruleColor: string;
  ruleWidth: number;
  titleFontSize: number;
  subtitleFontSize: number;
  subtitleColor: string;
  bodyFontSize: number;
  textColor: string;
  footerFontSize: number;
  footerColor: string;
  pageMargins: [number, number, number, number];
  tableHeaderFill: string;
  /**
   * LEGACY (kept for back-compat with persisted layouts): the rider tables now
   * all use the shared, JSON-safe `lightHorizontalLines` pdfmake layout (grey
   * 1pt row rules + a 2pt rule under the header), the same look as the
   * daysheet / tour-book tables. A per-line colour FUNCTION could never reach
   * the server renderer (the docDef is POSTed as JSON, which drops functions),
   * so this value never had any effect on the PDF.
   */
  tableLineColor: string;
  /**
   * Bold weight of the FIRST-PAGE title line (the band title suffix, or the
   * band name when no logo is shown). Default true — the historical look.
   */
  titleBold?: boolean;
  /**
   * Bold weight of the SUBTITLE lines: the tour name under the logo and the
   * version template line ("(version: van)"). Default true (the version line
   * used to be italic-only; it is now bold+italic unless unchecked).
   */
  subtitleBold?: boolean;
  /** Bold weight of the section headings (default true). */
  headingBold?: boolean;
  /** Show the band logo in the document header (default true). */
  showLogo?: boolean;
  /** Logo width in pt (default 180, min 40 / max 300 — setlist pattern). */
  logoWidth?: number;
  /**
   * UPPERCASE data cells (2026-10-17, setlist pattern): when true, the VALUES
   * of every rider table (input list, output list, backline, stands, rack
   * usage, fixtures, travel party) render in MAJUSCULES — applied AT RENDER
   * in buildPdfDoc's `dataCell`, never in the stored data nor the i18n values,
   * and the web UI keeps its case. Headers are already uppercase (headCell).
   */
  uppercase?: boolean;
  /**
   * VENUE HIGHLIGHT (2026-10-18, user): when true, table ROWS whose item is
   * provided by the VENUE get a subtle fill (venueHighlightColor, default a
   * very light yellow) across the rider tables that carry a provider —
   * Backline, Mic Stands and Wireless Transmitters. Applied AT RENDER in
   * buildPdfDoc's `dataCell` (replaces the zebra fill on those rows), never
   * in the stored data.
   */
  venueHighlight?: boolean;
  /** Fill used by `venueHighlight` (default a very light yellow). */
  venueHighlightColor?: string;
  /**
   * SHOW EMPTY ROWS (2026-10-18, user): per-section switch for the Input list
   * and the Output list — when on, each rack's channel range is padded with
   * empty rows up to the rack's declared inputs/outputs (the I/O racks of the
   * Audio specs config). Legacy default: Input list padded (the historical
   * "holes" rule), Output list not padded.
   */
  showEmptyRows?: boolean;
  /** Document font family (default 'Roboto' — the historical rendering). */
  fontFamily?: RiderFontFamily;
  /**
   * PER-FIELD keys, used by `PdfLayoutField.styleOverride` only (never by the
   * global or the section style — the section block does not expose them).
   * They are style keys of the same shape so a field override stays a
   * `Partial<PdfLayoutStyle>` (one single override model, one cascade).
   */
  /** Per-field bold weight of the field's text. */
  fieldBold?: boolean;
  /** Per-field italic of the field's text. */
  fieldItalic?: boolean;
  /** Per-field horizontal alignment of the field's text. */
  fieldAlign?: 'left' | 'center' | 'right';
}

/** Complete persisted layout for the rider PDF generator. */
export interface PdfLayout {
  version: 1;
  sections: PdfLayoutSection[];
  texts: PdfLayoutTexts;
  style: PdfLayoutStyle;
}

/** Section ids in the current hard-coded render order (default layout). */
export const DEFAULT_SECTION_ORDER: PdfSectionId[] = [
  'title',
  'contact',
  'intro',
  'general-info',
  'hospitality',
  'technical-rider',
  'sound',
  'iem',
  // Section MONITORS (2026-10-17, user) : les wedges/retours + subwoofer sont
  // des besoins monitor — section SÉPARÉE des IEM dans le rider ET l'editor.
  'monitors',
  'audio-backline',
  'wireless',
  'backline',
  'mic-stands',
  'mic-provided',
  'stageplan',
  'input-list',
  'output-list',
  'rack-usage',
  'stage-plot',
  'lights',
  'light-plot',
  'stage-design',
];

/** Default fixed texts — identical to the original hard-coded strings. */
export const DEFAULT_PDF_LAYOUT_TEXTS: PdfLayoutTexts = {
  titleSuffix: 'TECH & HOSPITALITY RIDER 2026',
  contactLabel: 'Management / Advancing Contact:',
  contactOverrideName: '',
  contactOverrideEmail: '',
  contactOverridePhone: '',
  introText:
    'This technical & hospitality rider forms an integral part of the contract it’s attached to. Non-respect of the requirements can lead to the cancellation of the show.',
  // NOTE: techIntroText is deliberately ABSENT from these defaults. The
  // effective technical intro is resolved at render time as:
  //   texts.techIntroText  (if the band's layout defines the key, even empty)
  //   → else SoundConfig.intro (legacy per-transport value, still the default
  //     for bands that never re-edited it)
  // → else the section auto-hides.
  // Providing a default here would print the generic clause instead of a band's
  // CUSTOMISED Sound intro. (v0.591)
  versionTemplate: '(version: {versionDetail})',
  footerLeftTemplate: '{bandName} TECH & HOSPITALITY RIDER 2026',
  footerCenterTemplate: '{versionLabel}',
  footerRightTemplate: 'PAGE {page} / {pages}',
  // Footer: ON by default (same look as before the toggle existed).
  footerEnabled: true,
  // Running page header: off by default (same look as before the feature).
  headerEnabled: false,
  headerLeftTemplate: '{bandName} TECH & HOSPITALITY RIDER 2026',
  headerCenterTemplate: '{versionLabel}',
  headerRightTemplate: 'PAGE {page} / {pages}',
  // Tour name under the logo: on by default (2026-08-11), toggleable in the
  // Layout Editor for bands that don't want it.
  showTourName: true,
};

/**
 * Default renderable fields per section, in engine render order. Each entry:
 * id (stable engine id), label (editor display), link (band-relative config
 * URL where the field's value is edited). Sections NOT listed here render as
 * a single monolithic block (tables, images, wrapper headings) — they have no
 * field-level granularity yet.
 */
export const DEFAULT_SECTION_FIELDS: Record<string, { id: string; label: string; link?: string }[]> = {
  // TECHNICAL RIDER — its own section above FOH & MON (v0.591). The technical
  // intro used to sit at the top of the Sound config/section; it is edited on the
  // General config page (band-level layout text, like the legal intro).
  'technical-rider': [
    { id: 'technical-rider.intro', label: 'Technical intro', link: 'config/general' },
  ],
  // Legal intro sentence: a SEPARATE section ABOVE General Information
  // (2026-09-16, Rider-Intro-Section-Spec). The old general-info.intro field
  // is migrated onto this section by mergePdfLayouts (back-compat).
  intro: [
    { id: 'intro.intro', label: 'Legal intro sentence', link: 'config/general' },
  ],
  'general-info': [
    { id: 'general-info.travel-party', label: 'Travel party (people on the road)', link: 'musicians' },
    { id: 'general-info.show-duration', label: 'Show duration', link: 'config/general' },
    { id: 'general-info.installation', label: 'Installation time', link: 'config/general' },
    { id: 'general-info.soundcheck', label: 'Soundcheck duration', link: 'config/general' },
    { id: 'general-info.changeover', label: 'Changeover', link: 'config/general' },
    { id: 'general-info.breakdown', label: 'Breakdown', link: 'config/general' },
    { id: 'general-info.security', label: 'Security', link: 'config/general' },
    { id: 'general-info.merchandise', label: 'Merchandising', link: 'config/general' },
    { id: 'general-info.special-requests', label: 'Local crew (stagehands, techs on site)', link: 'config/general' },
    { id: 'general-info.notes', label: 'Notes', link: 'config/general' },
  ],
  hospitality: [
    { id: 'hospitality.meals', label: 'Meals needed (total people)', link: 'config/hospitality' },
    { id: 'hospitality.food-allergies', label: 'Food allergies (from Team)', link: 'team' },
    { id: 'hospitality.non-food-allergies', label: 'Details about allergies', link: 'config/hospitality' },
    { id: 'hospitality.non-food-allergies-person', label: 'Non-food allergies (from Team)', link: 'team' },
    { id: 'hospitality.stage-towels', label: 'Stage towels', link: 'musicians' },
    { id: 'hospitality.dressing-towels', label: 'Dressing-room towels', link: 'musicians' },
    { id: 'hospitality.side-water', label: 'Side-stage water', link: 'musicians' },
    { id: 'hospitality.dressing-rooms', label: 'Dressing rooms', link: 'config/hospitality' },
    { id: 'hospitality.various', label: 'Various', link: 'config/hospitality' },
    { id: 'hospitality.security', label: 'Backstage security', link: 'config/hospitality' },
    { id: 'hospitality.breakfast', label: 'Breakfast', link: 'config/hospitality' },
    { id: 'hospitality.lunch', label: 'Lunch', link: 'config/hospitality' },
    { id: 'hospitality.dinner', label: 'Dinner', link: 'config/hospitality' },
    // Free-text "one item per line" lists (edited on the Hospitality config
    // page). Echoed in the PDF at the END of the section (2026-09-16): the user
    // wants them in the rider, and they carry content nothing else generates
    // (backstage catering list, dressing-room drinks). The layout field is what
    // makes them placeable / toggleable in the Rider Layout editor.
    { id: 'hospitality.catering', label: 'Catering', link: 'config/hospitality' },
    { id: 'hospitality.drinks', label: 'Drinks', link: 'config/hospitality' },
  ],
  sound: [
    { id: 'sound.foh', label: 'FOH Console', link: 'config/sound' },
    { id: 'sound.foh-requirements', label: 'FOH requirements', link: 'config/sound' },
    { id: 'sound.pa-mix-output', label: 'PA mix output', link: 'config/sound' },
    { id: 'sound.pa', label: 'PA System', link: 'config/sound' },
    { id: 'sound.monitor-console', label: 'Monitor Console', link: 'config/sound' },
    { id: 'sound.monitor-requirements', label: 'Monitor requirements', link: 'config/sound' },
    { id: 'sound.monitor', label: 'Monitor System', link: 'config/sound' },
    { id: 'sound.limit', label: 'Sound limit (dB)', link: 'config/sound' },
  ],
  iem: [
    { id: 'iem.system', label: 'System', link: 'config/iem' },
    { id: 'iem.mix', label: 'Mix (mono/stereo)', link: 'config/iem' },
    { id: 'iem.units', label: 'Units needed (people)', link: 'musicians' },
    { id: 'iem.frequency', label: 'Frequency Range', link: 'config/iem' },
    { id: 'iem.bodypacks', label: 'Bodypacks (transmitters + spare)', link: 'config/iem' },
    { id: 'iem.wired', label: 'Wired in-ears (no transmitter)', link: 'musicians' },
    { id: 'iem.note', label: 'Note', link: 'config/iem' },
  ],
  // Section MONITORS (2026-10-17, user) : les wedges/retours + subwoofer — des
  // besoins monitor, dans une section SÉPARÉE des IEM (rider + editor). Les ids
  // `iem.wedges`/`iem.sub` (v0.777, un jour en DEV) migrent ici avec back-compat.
  monitors: [
    { id: 'monitors.wedges', label: 'Monitors (wedges) — venue/band', link: 'config/iem' },
    { id: 'monitors.sub', label: 'Subwoofer — venue/band', link: 'config/iem' },
  ],
  'audio-backline': [
    // NOTE (v0.642, 2026-10-07): the Microphones and Frequency specs moved OUT
    // of this section — they live in the WIRELESS TRANSMITTERS section now
    // (ids `wireless.mics` / `wireless.frequency`), mirroring the config page
    // where both fields already sit inside its "Wireless Microphones" block.
    // mergePdfLayouts drops the legacy ids from any saved list and reinjects
    // their equivalents under `wireless`, carrying the user's enabled state.
    { id: 'audio-backline.cable', label: 'FOH Cables specs', link: 'config/audio-specs' },
    { id: 'audio-backline.stage-cable', label: 'Stage Cables specs', link: 'config/audio-specs' },
    // label corrigé le 2026-10-15 (user : « le champ audio cables se nomme encore
    // DMX ») : l'ID `audio-backline.dmx` est un vestige — il porte `audioCablesDetails`
    // (imprimé « Audio Cables » dans le PDF, libellé « Audio Cables Details » sur la
    // page config). ⚠️ NE PAS renommer l'ID : les layouts déjà enregistrés le citent.
    { id: 'audio-backline.dmx', label: 'Audio Cables Details', link: 'config/audio-specs' },
    { id: 'audio-backline.network', label: 'Network specs', link: 'config/audio-specs' },
    { id: 'audio-backline.power', label: 'Power specs', link: 'config/audio-specs' },
  ],
  // --- "Block" sections (2026-10-07, v0.640) ------------------------------
  // Wireless / Backline / Mic Stands / Microphones Provided By / Stage Plot
  // render as ONE monolithic block (a table, a text summary or an image) that
  // has no per-value config page of its own. Each gets a SINGLE "block" field
  // here: the field carries the WHOLE block, so it can be reordered / toggled
  // as one unit, and — crucially — it makes the section appear in the Rider
  // Layout editor with the "＋ Add space" / "＋ Add text" affordances, so the
  // free-text blocks and spacers inserted by the editor are now rendered too
  // (they used to be persisted but never emitted). The engine emits the section
  // title first, then follows this field order (spacer / free text / block).
  // Auto-hide is preserved: a block with no data only prints when at least one
  // enabled free-text field carries text; a lone spacer never prints a heading.
  wireless: [
    // The Microphones / Frequency specs moved here from AUDIO & BACKLINE
    // (v0.642, 2026-10-07): they are the two free-text spec lines of the
    // "Wireless Microphones" config block. Order mirrors the config page —
    // specs first, then the transmitters table (the section's "block" field).
    { id: 'wireless.mics', label: 'Microphones specs', link: 'config/audio-specs' },
    { id: 'wireless.frequency', label: 'Frequency specs', link: 'config/audio-specs' },
    { id: 'wireless.table', label: 'Wireless transmitters (table)' },
  ],
  // The Backline table is COLUMN-configurable (v0.641, 2026-10-07): its `fields`
  // list is interpreted as the table's COLUMNS (id = stable column id, label =
  // editor label, enabled = column visible, list order = column order), exactly
  // like Input List / Output List. Unlike those two PURE tables it also keeps
  // the "＋ Add space" / "＋ Add text" affordances (which those hide): the engine
  // emits the table AT THE POSITION of the first enabled column, spacers / texts
  // before it print ABOVE the table, the ones after print BELOW it.
  backline: [
    { id: 'backline.qty', label: 'Qty', link: 'config/backline' },
    { id: 'backline.type', label: 'Type', link: 'config/backline' },
    { id: 'backline.item', label: 'Description', link: 'config/backline' },
    { id: 'backline.model', label: 'Model', link: 'config/backline' },
    { id: 'backline.provided', label: 'Provided by', link: 'config/backline' },
    { id: 'backline.notes', label: 'Notes', link: 'config/backline' },
  ],
  // The Mic Stands table is COLUMN-configurable (v0.648, 2026-10-07): like
  // Backline (v0.641), its `fields` list IS the table's COLUMNS (id = stable
  // column id, enabled = visible, list order = column order) while KEEPING the
  // "＋ Add space" / "＋ Add text" affordances. The engine emits the table AT THE
  // POSITION of the first enabled column (spacers/texts before print above it,
  // after print below it). Default widths mirror the historical
  // `['auto','*','auto','auto']` (Qty / Boom / Provided by = short + bounded).
  // ⚠️ BUSINESS RULE (v0.647): a `stand: 'clip'` mic is NEVER a stand
  // (`isRealMicStand()` is the only gate) and the table stays filtered on the
  // stands provided by the venue — see `buildMicStands` in RiderPage.tsx.
  'mic-stands': [
    { id: 'mic-stands.qty', label: 'Qty' },
    { id: 'mic-stands.type', label: 'Type' },
    { id: 'mic-stands.boom', label: 'Boom' },
    // Destination (v0.651): WHO the venue-provided stand is for. The value is
    // DERIVED from the musician/crew carrying the instrument (the carrier is
    // known per track but was lost by the old type+boom aggregation) and can be
    // OVERRIDDEN by a free-text "Destination" set on the instrument track in
    // config. Declared between Boom and Provided by (reads best).
    { id: 'mic-stands.destination', label: 'Destination' },
    { id: 'mic-stands.provided', label: 'Provided by' },
  ],
  'mic-provided': [
    { id: 'mic-provided.summary', label: 'Provided-by summary' },
  ],
  'stage-plot': [
    { id: 'stage-plot.image', label: 'Stage plot (image)' },
  ],
  lights: [
    { id: 'lights.console', label: 'Console', link: 'config/lights' },
    { id: 'lights.haze', label: 'Haze Machine', link: 'config/lights' },
    { id: 'lights.fixtures', label: 'Fixtures (all lighting categories)', link: 'config/lights' },
    // The LIGHTS section MIXES TWO KINDS OF FIELDS (v0.648, 2026-10-07):
    //  - CONTENT LINES (console / haze / dmx / notes / video / power) each emit
    //    one text line, and `lights.fixtures` emits the fixtures TABLES;
    //  - the 4 FIXTURE COLUMNS below belong to the table emitted at the
    //    `lights.fixtures` position — they are NOT content lines and never emit
    //    anything on their own. The engine tells them apart with
    //    `LIGHT_FIXTURE_COLUMN_IDS`: the fixture tables are built from the
    //    ENABLED columns (order + widths) of this list. Unchecking
    //    `lights.fixtures` → no tables at all (unchanged); the 4 columns only
    //    toggle/reorder/resize the table.
    { id: 'lights.fixtures.qty', label: 'Fixtures: Qty' },
    { id: 'lights.fixtures.model', label: 'Fixtures: Model' },
    { id: 'lights.fixtures.description', label: 'Fixtures: Description' },
    { id: 'lights.fixtures.notes', label: 'Fixtures: Notes' },
    { id: 'lights.dmx', label: 'DMX line', link: 'config/lights' },
    { id: 'lights.notes', label: 'Notes', link: 'config/lights' },
    // Video format/resolution (two config fields on the Lights page, emitted as
    // ONE rider line) and Power requirements (`powerNotes`) used to have NO
    // layout field and were never printed in the rider PDF — added 2026-09-16 so
    // they are reported AND placeable/toggleable in the layout editor.
    { id: 'lights.video', label: 'Video format / resolution', link: 'config/lights' },
    { id: 'lights.power', label: 'Power requirements', link: 'config/lights' },
  ],
  // The Light Plot is an IMAGE (uploaded on the Lights config page). The single
  // field exists so the section is placeable / toggleable in the Rider Layout
  // editor like every other section; the builder still auto-hides the whole
  // section when no image is configured, and when this field is toggled off
  // (2026-09-16).
  'light-plot': [
    { id: 'light-plot.image', label: 'Light plot (image)', link: 'config/lights' },
  ],
  'stage-design': [
    { id: 'stage-design.dimensions', label: 'Stage dimensions', link: 'config/stage-design' },
    { id: 'stage-design.backdrop', label: 'Backdrop', link: 'config/stage-design' },
    { id: 'stage-design.backdrop-dimensions', label: 'Backdrop dimensions', link: 'config/stage-design' },
    { id: 'stage-design.risers', label: 'Risers', link: 'config/stage-design' },
    { id: 'stage-design.rug', label: 'Rug', link: 'config/stage-design' },
    { id: 'stage-design.accessories', label: 'Accessories', link: 'config/stage-design' },
    { id: 'stage-design.chairs', label: 'Chairs', link: 'config/stage-design' },
    { id: 'stage-design.tools', label: 'Tools', link: 'config/stage-design' },
    { id: 'stage-design.notes', label: 'Notes', link: 'config/stage-design' },
  ],
  // The Input List / Output List are TABLES, not free-form fields. Their
  // `fields` list is interpreted as the table's COLUMNS: id = stable column
  // id, label = header label, enabled = column visible, list order = column
  // order. This lets the layout editor show/hide/reorder the table columns
  // with the exact same field mechanism as the other sections. buildPdfDoc
  // reads only these known ids and ignores any spacer/text the editor adds.
  'input-list': [
    { id: 'input-list.ch', label: 'Ch' },
    { id: 'input-list.track', label: 'Track' },
    { id: 'input-list.rack', label: 'Rack' },
    { id: 'input-list.mic', label: 'Mic' },
    { id: 'input-list.connection', label: 'Connection' },
    { id: 'input-list.transmitter', label: 'Transmitter' },
    { id: 'input-list.stand', label: 'Stand' },
    { id: 'input-list.provided', label: 'Provided by' },
    { id: 'input-list.notes', label: 'Notes' },
  ],
  'output-list': [
    { id: 'output-list.rack', label: 'Rack' },
    { id: 'output-list.ch', label: 'Ch' },
    { id: 'output-list.track', label: 'Track' },
    { id: 'output-list.type', label: 'Type' },
    { id: 'output-list.notes', label: 'Notes' },
  ],
  'rack-usage': [
    { id: 'rack-usage.rack', label: 'Rack' },
    { id: 'rack-usage.inputs', label: 'Inputs' },
    { id: 'rack-usage.outputs', label: 'Outputs' },
    { id: 'rack-usage.occupants', label: 'Occupants' },
  ],
};

/**
 * The 4 COLUMN ids of the LIGHTS fixtures table (v0.648). The LIGHTS section
 * mixes CONTENT-LINE ids (console / haze / dmx / notes / video / power) — which
 * each emit one text line — with these 4 ids, which are the COLUMNS of the
 * fixture tables emitted at the `lights.fixtures` position. The engine uses this
 * list to tell the two kinds apart (the columns emit nothing on their own), and
 * the Rider Layout editor shows the width field ONLY on these 4 ids.
 */
export const LIGHT_FIXTURE_COLUMN_IDS = [
  'lights.fixtures.qty',
  'lights.fixtures.model',
  'lights.fixtures.description',
  'lights.fixtures.notes',
];

/**
 * id → CANONICAL editor label, built in ONE place from `DEFAULT_SECTION_FIELDS`
 * (the single source of truth). The Rider Layout editor MUST display a KNOWN
 * field's canonical label — a layout saved before a label was fixed (e.g. the
 * KADEBOSTANY layout stored `backline.item` with the stale label 'Item') must
 * render 'Description'. `mergePdfLayouts` also MIGRATES the stored label of
 * known ids to this value, so the persisted layout self-heals.
 *
 * ⚠️ Only KNOWN ids resolve here. A custom field the user created (a spacer /
 * free text, or any id absent from DEFAULT_SECTION_FIELDS) is NOT in the map →
 * callers fall back to the stored `label`, so user-authored fields keep their
 * own text.
 */
export const CANONICAL_FIELD_LABELS: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const defs of Object.values(DEFAULT_SECTION_FIELDS)) {
    for (const d of defs) m.set(d.id, d.label);
  }
  return m;
})();

/** The canonical label of a KNOWN field id, or `undefined` for an unknown id. */
export function canonicalFieldLabel(id: string): string | undefined {
  return CANONICAL_FIELD_LABELS.get(id);
}

/**
 * Traducteur minimal accepté par `fieldLabel` (compatible `t` de react-i18next).
 * Typé ici pour que le module reste sans dépendance à i18next.
 */
export type FieldLabelTranslator = (key: string, opts?: { defaultValue?: string }) => string;

/**
 * Clé i18n d'un champ du Rider Layout : l'`id` du champ, ses `.` remplacés par
 * `__`. i18next découpe toute clé sur `.` et un id comme `lights.fixtures` est
 * LUI-MÊME un champ : un arbre imbriqué serait ambigu (`lights.fixtures` serait
 * à la fois une feuille et un parent). Le sous-objet `riderLayoutFields` est donc
 * PLAT et cette fonction est la seule à construire la clé.
 */
export function riderLayoutFieldKey(id: string): string {
  return 'riderLayoutFields.' + id.split('.').join('__');
}

/**
 * Libellé AFFICHÉ d'un champ du Rider Layout editor.
 *
 * `DEFAULT_SECTION_FIELDS` reste la source des libellés CANONIQUES EN (les tests
 * purs et `mergePdfLayouts`, qui répare les layouts sauvegardés, en dépendent).
 * Cette fonction ne remplace rien : elle demande la traduction i18n
 * (`riderLayoutFieldKey`) et retombe sur le libellé canonique quand la clé
 * manque — l'UI parle donc FR/ES sans toucher au contrat de données.
 *
 * ⚠️ Un id INCONNU (champ créé par l'utilisateur) ne passe JAMAIS par i18n : son
 * libellé saisi est rendu tel quel.
 */
export function fieldLabel(
  id: string,
  fallbackLabel: string,
  t: FieldLabelTranslator,
): string {
  const canonical = canonicalFieldLabel(id);
  if (!canonical) return fallbackLabel;
  return t(riderLayoutFieldKey(id), { defaultValue: canonical });
}

/**
 * Default WIDTH of each TABLE-COLUMN field (v0.644), mirroring the engine's
 * defaults. Used ONLY by the Rider Layout editor to show a column's default
 * width as the numeric field's placeholder, and to describe the ↺ reset
 * ("back to the default"). A number = fixed pt; `'*'` = share the remaining
 * space; `'auto'` = natural content width (short + bounded columns only).
 *
 * ⚠️ MUST stay in sync with the builder: `INPUT_LIST_COLUMNS` /
 * `OUTPUT_LIST_COLUMNS` / `BACKLINE_COLUMNS` / `MIC_STANDS_COLUMNS` /
 * `LIGHT_FIXTURE_COLUMNS` in `src/pages/RiderPage.tsx` and the Rack-usage
 * `widths[]` there. A vitest (`riderColumnWidths.test.ts`) asserts this map
 * equals the engine definitions, so drift fails the build.
 */
export const DEFAULT_COLUMN_WIDTHS: Record<string, number | string> = {
  'input-list.ch': 22,
  'input-list.track': '*',
  'input-list.rack': 36,
  'input-list.mic': 36,
  'input-list.connection': 71,
  'input-list.transmitter': 76,
  'input-list.stand': 46,
  'input-list.provided': 59,
  'input-list.notes': '*',
  'output-list.rack': 90,
  'output-list.ch': 34,
  'output-list.track': '*',
  'output-list.type': 80,
  'output-list.notes': '*',
  'backline.qty': 'auto',
  'backline.type': '*',
  'backline.item': '*',
  'backline.model': '*',
  'backline.provided': 'auto',
  'backline.notes': '*',
  // Mic Stands columns (v0.648) — mirror the engine's historical widths.
  'mic-stands.qty': 'auto',
  'mic-stands.type': '*',
  'mic-stands.boom': 'auto',
  // Destination holds names / a free-text override → a BOUNDED numeric width
  // (never 'auto': unbounded, nor '*': that would stretch the table to full
  // page width and defeat the narrow-table centering, v0.651). 72 pt is the
  // MINIMUM that shows the full "DESTINATION" header on one line (11 chars ≈
  // 66 pt + 8 pt cell padding) AND keeps the widest real no-'*' table (SAMAEL,
  // which saved type=150/boom=150) inside the 515,3 pt usable width — proven by
  // a real render (scripts/qa-rider-center-tables.ts). A numeric width WRAPS
  // long values instead of clipping them.
  'mic-stands.destination': 72,
  'mic-stands.provided': 'auto',
  // Lights fixtures columns (v0.648) — mirror the engine's [30,'*','*','*'].
  'lights.fixtures.qty': 30,
  'lights.fixtures.model': '*',
  'lights.fixtures.description': '*',
  'lights.fixtures.notes': '*',
  'rack-usage.rack': 90,
  'rack-usage.inputs': 60,
  'rack-usage.outputs': 70,
  'rack-usage.occupants': '*',
};

/** Default style — identical to the original hard-coded pdfmake styles. */
export const DEFAULT_PDF_LAYOUT_STYLE: PdfLayoutStyle = {
  headingColor: '#222222',
  headingFontSize: 13.5,
  ruleColor: '#9aa0ab',
  ruleWidth: 515,
  titleFontSize: 18,
  subtitleFontSize: 11,
  subtitleColor: '#666',
  bodyFontSize: 10,
  textColor: '#333333',
  footerFontSize: 8,
  footerColor: '#888',
  pageMargins: [40, 40, 40, 50],
  tableHeaderFill: '#f0f0f0',
  tableLineColor: '#ccc',
  // Text weights (2026-09-15): all three default to TRUE so the default render
  // keeps the historical look (headings/title/tour name were already bold) —
  // except the version template line, which becomes bold+italic by default.
  // Exposed in the Rider Layout editor's Style panel.
  titleBold: true,
  subtitleBold: true,
  headingBold: true,
  venueHighlight: false,
  venueHighlightColor: '#FFF9C4',
};

/**
 * Style keys the per-field "Aa" block exposes (Font / Size / Bold / Italic /
 * Color / Align). A field override is a `Partial<PdfLayoutStyle>`, but only
 * these keys make sense for ONE field — and only these are written by the
 * editor.
 */
export const PDF_FIELD_STYLE_KEYS = [
  'fontFamily',
  'bodyFontSize',
  'fieldBold',
  'fieldItalic',
  'textColor',
  'fieldAlign',
] as const;
export type PdfFieldStyleKey = (typeof PDF_FIELD_STYLE_KEYS)[number];

/**
 * The EXPLICIT style override of ONE field — the keys the field actually
 * overrides, i.e. what the builder must stamp on that field's nodes:
 * `global style → section.styleOverride → field.styleOverride` (the field
 * wins), where the historical flat keys of a free-text field are read as the
 * soft default and `styleOverride` overrides them key by key:
 *
 *   field.fontSize → bodyFontSize   field.bold   → fieldBold
 *   field.italic   → fieldItalic    field.align  → fieldAlign
 *
 * Returns `null` when the field overrides nothing: the builder then leaves the
 * field's nodes untouched, so the default render stays byte-identical.
 */
export function fieldStyleOverride(
  field: PdfLayoutField | null | undefined,
): Partial<PdfLayoutStyle> | null {
  if (!field) return null;
  const out: Partial<PdfLayoutStyle> = {};
  // 1. LEGACY flat keys (free-text fields saved before the per-field style
  //    block existed) — mapped onto their style key.
  if (field.fontSize !== undefined && field.fontSize !== null) out.bodyFontSize = field.fontSize;
  if (field.bold !== undefined) out.fieldBold = field.bold;
  if (field.italic !== undefined) out.fieldItalic = field.italic;
  if (field.align !== undefined) out.fieldAlign = field.align;
  // 2. The explicit per-field override wins over the legacy keys.
  const ov = field.styleOverride;
  if (ov) {
    for (const key of Object.keys(ov) as PdfFieldStyleKey[]) {
      const value = ov[key];
      if (value === undefined || value === null) continue;
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * The default layout = EXACT current behaviour of buildPdfDoc: all 17 sections
 * enabled in the current render order; Sound and Stage Plot start a new page.
 */
export function createDefaultPdfLayout(): PdfLayout {
  return {
    version: 1,
    sections: DEFAULT_SECTION_ORDER.map((id) => ({
      id,
      enabled: true,
      title: null,
      pageBreakBefore: id === 'sound' || id === 'stageplan' || id === 'stage-plot',
      fields: (DEFAULT_SECTION_FIELDS[id] || []).map((f) => ({
        id: f.id,
        enabled: true,
        label: f.label,
        link: f.link ?? null,
      })),
    })),
    texts: { ...DEFAULT_PDF_LAYOUT_TEXTS },
    style: { ...DEFAULT_PDF_LAYOUT_STYLE },
  };
}

/**
 * Merge a persisted (partial) layout override over the defaults.
 * - texts/style: shallow-merged per key (override wins, defaults fill gaps).
 * - sections: the override array wins WHOLESALE (it is the full ordered list);
 *   when absent the default sections are kept.
 * - section.fields: when a section has NO fields array (legacy layout saved
 *   before field-level editing), backfill from the section's default fields so
 *   the engine and editor always have a full field list.
 */
export function mergePdfLayouts(
  base: PdfLayout,
  override: Partial<PdfLayout> | null,
): PdfLayout {
  if (!override) return base;
  const defaultFieldsFor = (id: PdfSectionId) =>
    (DEFAULT_SECTION_FIELDS[id] || []).map((f) => ({
      id: f.id,
      enabled: true,
      label: f.label,
      link: f.link ?? null,
    }));
  // Insert the default fields a saved list is missing AT THEIR DEFAULT POSITION
  // (never appended at the end): a layout saved before a field was introduced
  // must render it where the default layout puts it. Saved fields keep their
  // relative order, so a genuine ↑/↓ reorder by the admin is preserved.
  const insertFieldsAtDefaultPositions = (
    saved: PdfLayoutField[],
    defaults: PdfLayoutField[],
  ): PdfLayoutField[] => {
    const defaultIdx = new Map(defaults.map((d, i) => [d.id, i]));
    const savedIds = new Set(saved.map((f) => f.id));
    const pending = defaults.filter((d) => !savedIds.has(d.id));
    if (pending.length === 0) return saved;
    const out: PdfLayoutField[] = [];
    let pi = 0;
    for (const f of saved) {
      const idx = defaultIdx.get(f.id);
      // A saved field unknown to the defaults (spacer / free text / removed
      // field) does not move the cursor: pending fields stay anchored to the
      // last known default field.
      if (idx !== undefined) {
        while (pi < pending.length && (defaultIdx.get(pending[pi].id) ?? Infinity) < idx) {
          out.push(pending[pi++]);
        }
      }
      out.push(f);
    }
    while (pi < pending.length) out.push(pending[pi++]);
    return out;
  };
  // Back-compat (v0.642): the Microphones (`audio-backline.mics`) and Frequency
  // (`audio-backline.frequency`) spec fields were MOVED from Audio & Backline to
  // the Wireless Transmitters section (as `wireless.mics` / `wireless.frequency`).
  // A layout saved before this release keeps both ids under `audio-backline`:
  // drop them there (dead toggles) and carry their `enabled` / `label` /
  // `styleOverride` onto the new `wireless.*` entries below, so a field the user
  // had unchecked stays unchecked.
  const savedSections = override.sections ?? base.sections;
  const legacyAbFields =
    savedSections.find((s) => (s.id as string) === 'audio-backline')?.fields || [];
  const legacyAbMics = legacyAbFields.find((f) => f.id === 'audio-backline.mics');
  const legacyAbFreq = legacyAbFields.find((f) => f.id === 'audio-backline.frequency');
  const sections = (override.sections ?? base.sections).map((s) => {
    // Back-compat: the first-page title block was renamed 'header' -> 'title'
    // (2026-08-10). Layouts saved under the old name still work.
    const id = (s.id as string) === 'header' ? 'title' : s.id;
    let savedFields = Array.isArray(s.fields) && s.fields.length > 0 ? s.fields : null;
    // Back-compat (Rider-Intro-Section-Spec, 2026-09-16): the legal intro
    // sentence moved out of General Information into its own 'intro' section.
    // Drop the obsolete 'general-info.intro' field from any saved list so the
    // editor stops showing a dead toggle — the value now lives under
    // 'intro.intro' (both read the same texts.introText).
    if (id === 'general-info' && savedFields) {
      savedFields = savedFields.filter((f) => f.id !== 'general-info.intro');
    }
    // Back-compat (v0.591): the technical intro moved out of the Sound section
    // into its own 'technical-rider' section (above FOH & MON). Drop the obsolete
    // 'sound.intro' field from any saved list so the editor stops showing a dead
    // toggle — the text now lives in texts.techIntroText.
    if (id === 'sound' && savedFields) {
      savedFields = savedFields.filter((f) => f.id !== 'sound.intro');
    }
    // Back-compat (v0.618): the Floor Kit section was retired from the Lights
    // config page (its 4 totals duplicated the fixture lists, 2026-09-27). Drop
    // the obsolete 'lights.floor-kit' field from any saved list so the editor
    // stops showing a dead toggle — the stored `lights.<transport>.floorKit`
    // values stay untouched in rider_config, they are simply never rendered
    // again.
    if (id === 'lights' && savedFields) {
      savedFields = savedFields.filter((f) => f.id !== 'lights.floor-kit');
    }
    // Back-compat (v0.641): Backline's single "block" field ('backline.table')
    // was replaced by 6 configurable COLUMNS. Drop the obsolete id from any
    // saved list so the editor stops showing a dead toggle — the 6 columns are
    // injected at their default positions by insertFieldsAtDefaultPositions
    // below, and the engine renders the table at the position of the first
    // enabled column.
    if (id === 'backline' && savedFields) {
      savedFields = savedFields.filter((f) => f.id !== 'backline.table');
    }
    // Back-compat (v0.648): Mic Stands' single "block" field
    // ('mic-stands.table') was replaced by 4 configurable COLUMNS. Drop the
    // obsolete id from any saved list (dead toggle) — the 4 columns are
    // injected at their default positions by insertFieldsAtDefaultPositions
    // below, and the engine renders the table at the position of the first
    // enabled column (same model as Backline v0.641).
    if (id === 'mic-stands' && savedFields) {
      savedFields = savedFields.filter((f) => f.id !== 'mic-stands.table');
    }
    // Back-compat (v0.642): the Microphones / Frequency specs moved to the
    // Wireless section — drop their legacy ids from a saved Audio & Backline
    // list so the layout editor stops showing a dead toggle.
    if (id === 'audio-backline' && savedFields) {
      savedFields = savedFields.filter(
        (f) => f.id !== 'audio-backline.mics' && f.id !== 'audio-backline.frequency',
      );
    }
    // Back-compat (2026-10-17): the Monitors (wedges) + Subwoofer fields moved
    // OUT of the IEM section into their own 'monitors' section. A layout saved
    // while they lived under `iem` keeps those ids under IEM: drop them there
    // (dead toggles) and carry their `enabled` / `label` / `styleOverride`
    // onto the new `monitors.*` entries below, so a field the user had
    // unchecked stays unchecked. (Same model as the v0.642 wireless.* move.)
    const legacyIemMonitor: Record<string, string> = { 'iem.wedges': 'monitors.wedges', 'iem.sub': 'monitors.sub' };
    let legacyIemCarry: Record<string, PdfLayoutField> = {};
    if (id === 'iem' && savedFields) {
      legacyIemCarry = {};
      for (const [oldId, newId] of Object.entries(legacyIemMonitor)) {
        const f = savedFields.find((x) => x.id === oldId);
        if (f) {
          legacyIemCarry[newId] = f;
          savedFields = savedFields.filter((x) => x.id !== oldId);
        }
      }
    }
    if (id === 'monitors' && savedFields) {
      savedFields = savedFields.map((f) => legacyIemCarry[f.id] ?? f);
    }
    const defaults = defaultFieldsFor(id as PdfSectionId);
    // When the layout has a saved field list, INSERT the default fields it is
    // missing at their DEFAULT position (they used to be appended at the END,
    // which silently mis-ordered them: e.g. a layout saved before 'sound.intro'
    // existed ended up with the Sound intro rendered last once field order
    // became effective). The saved relative order — a real ↑/↓ reorder — is
    // preserved either way.
    const fields = savedFields ? insertFieldsAtDefaultPositions(savedFields, defaults) : defaults;
    // Back-compat (v0.642): seed the moved Microphones / Frequency specs under
    // `wireless`, carrying the state of the legacy Audio & Backline entries. Only
    // entries NOT already present in the saved wireless list are seeded, so once
    // the editor autosaves the migrated layout this becomes a no-op and a later
    // user choice (uncheck) is never overridden by the stale legacy entry.
    let effectiveFields = fields;
    if (id === 'wireless') {
      const savedIds = new Set((savedFields || []).map((f) => f.id));
      effectiveFields = fields.map((f) => {
        if (savedIds.has(f.id)) return f;
        const legacy = f.id === 'wireless.mics' ? legacyAbMics
          : f.id === 'wireless.frequency' ? legacyAbFreq
          : undefined;
        if (!legacy) return f;
        return {
          ...f,
          enabled: legacy.enabled !== false,
          ...(legacy.label ? { label: legacy.label } : {}),
          ...(legacy.styleOverride ? { styleOverride: legacy.styleOverride } : {}),
        };
      });
    }
    // Fix links that point to pages that no longer exist: the Schedule config
    // page was merged into General (2026-08-10), so any field whose link is
    // 'config/schedule' is remapped to 'config/general'. Likewise the merged
    // 'config/audio-backline' page was split into Audio specs / Backline
    // (v0.645): every saved 'config/audio-backline' link is remapped to the
    // Audio specs page (the Backline fields now carry 'config/backline').
    // Finally, a saved field that carries NO link but whose default now defines
    // one adopts the default (e.g. the 6 Backline columns saved in v0.641 had
    // no link; they now point to the Backline page). Links are never user-edited
    // so this only ADDS a link where there was none.
    const defaultLinkById = new Map<string, string>();
    for (const defs of Object.values(DEFAULT_SECTION_FIELDS)) {
      for (const d of defs) if (d.link) defaultLinkById.set(d.id, d.link);
    }
    // Canonical LABEL migration (v0.651): refresh the stored label of a KNOWN
    // field id to its canonical value (single source = DEFAULT_SECTION_FIELDS).
    // This self-heals a layout saved with a now-stale label (the KADEBOSTANY
    // layout stored `backline.item` as 'Item'; the canonical label is
    // 'Description'). UNKNOWN ids (user spacers / free text, or a removed id)
    // keep their own label — only ids present in the defaults are touched.
    // IDEMPOTENT: the canonical value maps to itself, so a second pass is a
    // no-op.
    const defaultLabelById = new Map<string, string>();
    for (const defs of Object.values(DEFAULT_SECTION_FIELDS)) {
      for (const d of defs) defaultLabelById.set(d.id, d.label);
    }
    const normalized = effectiveFields.map((f) => {
      let link = f.link;
      if (link === 'config/schedule') link = 'config/general';
      else if (link === 'config/audio-backline') link = 'config/audio-specs';
      if (!link && defaultLinkById.has(f.id)) link = defaultLinkById.get(f.id)!;
      const canonicalLabel = defaultLabelById.get(f.id);
      const label = canonicalLabel !== undefined ? canonicalLabel : f.label;
      return link === f.link && label === f.label ? f : { ...f, link, label };
    });
    return { ...s, id, fields: normalized };
  });
  // Back-compat (v0.515): a layout saved before a NEW section was added to the
  // default order (e.g. 'rack-usage') won't contain it. Append any default
  // section missing from the saved list, at its default position, so the PDF
  // and Rider Layout editor always expose the full section set. Sections already
  // present (matched by id after the header→title remap) are never duplicated,
  // and the relative order of saved non-default sections is preserved.
  const presentIds = new Set(sections.map((s) => s.id));
  const defaultById = new Map(
    createDefaultPdfLayout().sections.map((d) => [d.id, d]),
  );
  const defaultIndex = new Map(DEFAULT_SECTION_ORDER.map((id, i) => [id, i]));
  // Insert missing default sections (e.g. 'rack-usage' added in v0.515) into the
  // saved list at their DEFAULT position — before the first saved section whose
  // default index is greater — while preserving the relative order of the saved
  // sections (including a custom reorder like "lights first").
  const pendingDefaults = DEFAULT_SECTION_ORDER.filter(
    (id) => defaultById.has(id) && !presentIds.has(id),
  );
  const sectionsWithDefaults: PdfLayoutSection[] = [];
  for (const s of sections) {
    const idx = defaultIndex.get(s.id) ?? Infinity;
    while (
      pendingDefaults.length &&
      (defaultIndex.get(pendingDefaults[0]) ?? Infinity) < idx
    ) {
      sectionsWithDefaults.push(defaultById.get(pendingDefaults.shift()!)!);
    }
    sectionsWithDefaults.push(s);
  }
  for (const id of pendingDefaults) sectionsWithDefaults.push(defaultById.get(id)!);
  return {
    version: override.version ?? base.version,
    sections: sectionsWithDefaults,
    texts: { ...base.texts, ...(override.texts ?? {}) },
    style: { ...base.style, ...(override.style ?? {}) },
  };
}
